import React, {useEffect, useState} from 'react'
import axios from 'axios'
import {useNavigate} from 'react-router-dom'

const API_URL = process.env.REACT_APP_API_URL
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`
const GET_FACULTIES_URL = `${API_URL}/Custom/GetFaculties`
const GET_DEPARTMENTS_URL = `${API_URL}/Custom/GetDepartmentsByFaculty`
const GET_SCHEDULE_BY_CLASS_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClass`
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`
const GET_ROOMS_URL = `${API_URL}/Custom/GetRooms`
const GET_SCHEDULE_BY_CLASSROOM_URL = `${API_URL}/Custom/GetUniversityWeeklyScheduleByClassroom`

interface Option {
  id: number
  name: string
  isActive?: boolean
}

interface ScheduleItem {
  id: number
  day: number
  startTime: string
  endTime: string
  courseName: string
  courseCode?: string
  classroomName: string
  name?: string
  detail?: string
  isEvent?: boolean
  eventDate?: string | null
}

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']
const dayColors = [
  'bg-primary',
  'bg-secondary',
  'bg-success',
  'bg-danger',
  'bg-warning',
  'bg-info',
  'bg-dark',
]

const PublicUniversityCourseScheduleList: React.FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'class' | 'classroom'>('class')
  const [selectedSemester, setSelectedSemester] = useState<number | null>(null)
  const [semesterName, setSemesterName] = useState<string>('')
  const [faculties, setFaculties] = useState<Option[]>([])
  const [selectedFaculty, setSelectedFaculty] = useState<number | null>(null)
  const [departments, setDepartments] = useState<Option[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null)
  const [schedule, setSchedule] = useState<ScheduleItem[]>([])
  const [buildings, setBuildings] = useState<Option[]>([])
  const [selectedBuilding, setSelectedBuilding] = useState<number | null>(null)
  const [classrooms, setClassrooms] = useState<Option[]>([])
  const [selectedClassroom, setSelectedClassroom] = useState<number | null>(null)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date()
    const dow = (today.getDay() + 6) % 7
    const start = new Date(today)
    start.setDate(today.getDate() - dow)
    start.setHours(0, 0, 0, 0)
    return start
  })

  useEffect(() => {
    axios
      .get(GET_SEMESTERS_URL)
      .then((res) => {
        const data: Option[] = res.data || []
        const active = data.find((s) => s.isActive) || data[0]
        if (active) {
          setSelectedSemester(active.id)
          setSemesterName(active.name)
        }
      })
      .catch(console.error)
  }, [])

  useEffect(() => {
    if (!selectedSemester) return
    if (activeTab === 'class') {
      axios
        .get(GET_FACULTIES_URL)
        .then((res) =>
          setFaculties((res.data || []).filter((f: Option) => f.name !== 'Tıp Fakültesi'))
        )
        .catch(console.error)
      setSelectedFaculty(null)
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
    } else {
      axios
        .get(GET_BUILDINGS_URL)
        .then((res) => setBuildings(res.data || []))
        .catch(console.error)
      setSelectedBuilding(null)
      setClassrooms([])
      setSelectedClassroom(null)
    }
    setSchedule([])
    setWeekStart(() => {
      const today = new Date()
      const dow = (today.getDay() + 6) % 7
      const start = new Date(today)
      start.setDate(today.getDate() - dow)
      start.setHours(0, 0, 0, 0)
      return start
    })
  }, [selectedSemester, activeTab])

  useEffect(() => {
    if (activeTab !== 'class') return
    if (!selectedFaculty) {
      setDepartments([])
      setSelectedDepartment(null)
      setSelectedGrade(null)
      setSchedule([])
      return
    }
    axios
      .get(`${GET_DEPARTMENTS_URL}/${selectedFaculty}`)
      .then((res) => setDepartments(res.data || []))
      .catch(console.error)
    setSelectedDepartment(null)
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedFaculty, activeTab])

  useEffect(() => {
    if (activeTab !== 'class') return
    setSelectedGrade(null)
    setSchedule([])
  }, [selectedDepartment, activeTab])

  useEffect(() => {
    if (activeTab !== 'classroom') return
    if (!selectedBuilding) {
      setClassrooms([])
      setSelectedClassroom(null)
      setSchedule([])
      return
    }
    axios
      .get(`${GET_ROOMS_URL}?buildingId=${selectedBuilding}&useExamCapacity=false`)
      .then((res) => setClassrooms(res.data || []))
      .catch(console.error)
    setSelectedClassroom(null)
    setSchedule([])
  }, [selectedBuilding, activeTab])

  const loadSchedule = () => {
    if (activeTab === 'class') {
      if (!selectedSemester || !selectedFaculty || !selectedDepartment || !selectedGrade) return
      axios
        .get(
          `${GET_SCHEDULE_BY_CLASS_URL}?semesterId=${selectedSemester}&classId=${selectedGrade}&departmentId=${selectedDepartment}`
        )
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({
              ...i,
              eventDate: i.eventDate || i.EventDate || null,
            }))
          )
        )
        .catch(console.error)
    } else {
      if (!selectedSemester || !selectedClassroom) return
      axios
        .get(
          `${GET_SCHEDULE_BY_CLASSROOM_URL}?semesterId=${selectedSemester}&classroomId=${selectedClassroom}`
        )
        .then((res) =>
          setSchedule(
            (res.data || []).map((i: any) => ({
              ...i,
              eventDate: i.eventDate || i.EventDate || null,
            }))
          )
        )
        .catch(console.error)
    }
  }

  const renderTable = () => {
    if (!schedule.length) return null
    const grouped = schedule.reduce((acc: Record<number, ScheduleItem[]>, item) => {
      if (!acc[item.day]) acc[item.day] = []
      acc[item.day].push(item)
      return acc
    }, {})
    return (
      <div className='row'>
        {Object.entries(grouped).map(([day, items]) => (
          <div key={day} className='col-md-6 col-lg-4 mb-4'>
            <h5 className={`p-2 rounded text-center text-white ${dayColors[Number(day) - 1]}`}>
              {dayNames[Number(day) - 1]}
            </h5>
            <table className='table table-bordered table-hover'>
              <thead className='table-light'>
                <tr>
                  <th className='text-center'>Saat</th>
                  <th>Ders</th>
                  <th>Tür</th>
                  <th>Sınıf</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td className='text-center'>
                      <span className='badge bg-info'>
                        {i.startTime} - {i.endTime}
                      </span>
                    </td>
                    <td>{i.courseCode ? `${i.courseName} (${i.courseCode})` : i.courseName}</td>
                    <td>{i.name ? i.name.split(' - ').pop() : ''}</td>
                    <td>{i.classroomName}</td>
                    <td>{i.detail || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    )
  }

  const renderClassroomCalendar = () => {
    if (!schedule.length) return null
    const days: Date[] = Array.from({length: 7}, (_, i) => {
      const d = new Date(weekStart)
      d.setDate(weekStart.getDate() + i)
      return d
    })
    const toKey = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    const formatDate = (d: Date) =>
      d.toLocaleDateString('tr-TR', {weekday: 'short', day: 'numeric', month: 'numeric'})

    const itemsForDate = (d: Date) => {
      const key = toKey(d)
      const dow = ((d.getDay() + 6) % 7) + 1
      return schedule
        .filter((s) => {
          if (s.eventDate) {
            const ev = (s.eventDate || '').split('T')[0]
            return ev === key
          }
          return s.day === dow
        })
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
    }

    return (
      <div>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <button
            className='btn btn-light btn-sm'
            onClick={() =>
              setWeekStart((prev) => {
                const d = new Date(prev)
                d.setDate(d.getDate() - 7)
                return d
              })
            }
          >
            Önceki Hafta
          </button>
          <div className='fw-semibold'>
            {toKey(days[0])} - {toKey(days[days.length - 1])}
          </div>
          <button
            className='btn btn-light btn-sm'
            onClick={() =>
              setWeekStart((prev) => {
                const d = new Date(prev)
                d.setDate(d.getDate() + 7)
                return d
              })
            }
          >
            Sonraki Hafta
          </button>
        </div>
        <div className='row'>
          {days.map((d) => {
            const items = itemsForDate(d)
            return (
              <div key={toKey(d)} className='col-md-4 col-lg-3 mb-3'>
                <div className='border rounded h-100'>
                  <div className='p-2 bg-light fw-semibold text-center'>{formatDate(d)}</div>
                  <div className='p-2'>
                    {items.length === 0 && <div className='text-muted small'>Kayıt yok</div>}
                    {items.map((i, idx) => (
                      <div
                        key={idx}
                        className={`mb-2 p-2 border rounded ${
                          i.isEvent ? 'border-warning bg-warning bg-opacity-10' : ''
                        }`}
                      >
                        <div className='d-flex justify-content-between'>
                          <span className='fw-semibold'>
                            {i.isEvent
                              ? i.courseName
                              : i.courseCode
                              ? `${i.courseName} (${i.courseCode})`
                              : i.courseName}
                          </span>
                          <span className='badge bg-light text-dark border'>
                            {i.startTime} - {i.endTime}
                          </span>
                        </div>
                        <div className='small text-muted mt-1'>
                          {i.isEvent ? 'Etkinlik' : i.name ? i.name.split(' - ').pop() : ''}
                          {i.isEvent && i.eventDate ? ` • ${i.eventDate}` : ''}
                        </div>
                        {i.detail && <div className='small text-muted mt-1'>{i.detail}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='container mt-4'>
      <h2 className='mb-4'>
        {semesterName ? `${semesterName} Dönemi Haftalık Ders Programı` : 'Haftalık Ders Programı'}
      </h2>
      <div className='alert alert-primary'>
        Ortak derslere rektörlük öğrenci işleri seçerek ulaşmanız gerekmektedir. OSD kodlu derslerde
        sınıfınız 1 olmasa dahi sınıf seçimini 1 olarak yapınız.
      </div>
      <ul className='nav nav-tabs mb-3'>
        <li className='nav-item'>
          <button
            className={`nav-link ${activeTab === 'class' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('class')
              setSchedule([])
            }}
          >
            Bölüm
          </button>
        </li>
        <li className='nav-item'>
          <button
            className={`nav-link ${activeTab === 'classroom' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('classroom')
              setSchedule([])
            }}
          >
            Derslik
          </button>
        </li>
      </ul>

      {activeTab === 'class' && (
        <>
          {faculties.length > 0 && (
            <div className='mb-3'>
              <label>Fakülte</label>
              <select
                className='form-control'
                value={selectedFaculty || ''}
                onChange={(e) => setSelectedFaculty(Number(e.target.value))}
              >
                <option value='' disabled>
                  Seçiniz
                </option>
                {faculties.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {departments.length > 0 && (
            <div className='mb-3'>
              <label>Bölüm</label>
              <select
                className='form-control'
                value={selectedDepartment || ''}
                onChange={(e) => setSelectedDepartment(Number(e.target.value))}
              >
                <option value='' disabled>
                  Seçiniz
                </option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className='mb-3'>
            <label>Sınıf</label>
            <select
              className='form-control'
              value={selectedGrade || ''}
              onChange={(e) => setSelectedGrade(Number(e.target.value))}
            >
              <option value='' disabled>
                Seçiniz
              </option>
              {[1, 2, 3, 4, 5, 6].map((g) => (
                <option key={g} value={g}>
                  {g}. Sınıf
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {activeTab === 'classroom' && (
        <>
          {buildings.length > 0 && (
            <div className='mb-3'>
              <label>Bina</label>
              <select
                className='form-control'
                value={selectedBuilding || ''}
                onChange={(e) => setSelectedBuilding(Number(e.target.value))}
              >
                <option value='' disabled>
                  Seçiniz
                </option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {classrooms.length > 0 && (
            <div className='mb-3'>
              <label>Derslik</label>
              <select
                className='form-control'
                value={selectedClassroom || ''}
                onChange={(e) => setSelectedClassroom(Number(e.target.value))}
              >
                <option value='' disabled>
                  Seçiniz
                </option>
                {classrooms.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <div className='mb-4'>
        <button className='btn btn-primary me-2' onClick={loadSchedule}>
          Listele
        </button>
        {activeTab === 'class' && schedule.length > 0 && (
          <button
            className='btn btn-secondary'
            onClick={() => {
              navigate(`/program/print/${selectedSemester}/${selectedDepartment}/${selectedGrade}`)
            }}
          >
            PDF İndir
          </button>
        )}
        {activeTab === 'classroom' && schedule.length > 0 && (
          <button
            className='btn btn-secondary'
            onClick={() => {
              navigate(`/program/print-classroom/${selectedSemester}/${selectedClassroom}`)
            }}
          >
            PDF İndir
          </button>
        )}
      </div>
      {activeTab === 'class' ? renderTable() : renderClassroomCalendar()}
    </div>
  )
}

export default PublicUniversityCourseScheduleList
